// =============================================================================
//  L'ANNUAIRE — un écran à lui, où l'on CORRIGE
// =============================================================================
//  🔴 CE QU'IL RÉPARE. L'annuaire n'existait qu'À L'INTÉRIEUR du parcours
//  d'envoi : on pouvait y ajouter une fiche, ⛔ jamais la relire, la corriger
//  ni la retirer. Un annuaire qu'on ne peut pas corriger porte ses adresses
//  fausses jusqu'à ce qu'un pli parte au mauvais service — et DEC-025 dit que
//  le choix des destinataires reste HUMAIN, ce qui suppose des données que
//  l'humain peut redresser.
//
//  🔑 RETIRER ≠ DÉTRUIRE. Une adresse a pu RECEVOIR des plis : l'effacer
//  rendrait illisible l'historique qui la nomme. L'écran mesure donc, fiche par
//  fiche, combien de transmissions s'y appuient, et il le DIT AVANT le geste —
//  ⛔ plutôt que de laisser découvrir un refus après avoir cliqué.
//    · aucune transmission ⇒ la fiche s'efface pour de bon (erreur de saisie)
//    · au moins une        ⇒ désactivation seule, et le refus dit COMBIEN
//
//  ⚠️ Corriger l'ADRESSE elle-même n'est pas un ré-enregistrement : l'adresse
//  est la clé. Le serveur crée la nouvelle, DÉSACTIVE l'ancienne et les lie
//  dans les deux sens. Rien ne disparaît, et une correction faite par erreur se
//  défait en réactivant l'ancienne.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Search, UserPlus, Pencil, EyeOff, Eye, Trash2, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const BASE = `${API}/api/metier/habilitation`;

const TYPES = [
  { v: "generique", t: "Adresse du service",
    d: "Un dépôt part vers le service qui l'enregistre." },
  { v: "nominatif", t: "Contact nominatif",
    d: "Une réponse à un complément part vers l'agent qui l'a demandé." },
];

const TON_TYPE = {
  generique: { bg: "var(--oh-stat-blue-bg)", text: "var(--oh-stat-blue-text)" },
  nominatif: { bg: "var(--oh-stat-purple-bg)", text: "var(--oh-stat-purple-text)" },
};

const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const champStyle = {
  height: 36, borderRadius: 8, padding: "0 10px", fontSize: 13, width: "100%",
  background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
  border: "0.5px solid var(--oh-border)",
};
const btnDiscret = {
  display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px",
  borderRadius: 8, fontSize: 12, cursor: "pointer", background: "transparent",
  border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)",
};
const btnPrimaire = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px",
  borderRadius: 8, fontSize: 13, cursor: "pointer", border: "none",
  background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)",
};

const FICHE_VIDE = { organisme: "", email: "", nom: "", fonction: "",
                     departement: "", type: "generique" };

export default function AnnuaireEcran({ onErreur }) {
  const [fiches, setFiches] = useState([]);
  const [usages, setUsages] = useState({});
  const [chargement, setChargement] = useState(true);
  const [inclureInactifs, setInclureInactifs] = useState(true);
  const [filtre, setFiltre] = useState("");
  const [edition, setEdition] = useState(null);     // fiche en cours d'édition
  const [creation, setCreation] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      // ⚠️ Les INACTIVES sont incluses par défaut : c'est un écran de gestion.
      // ⛔ Les masquer ferait croire qu'une fiche désactivée a disparu, et on
      // la ressaisirait — en double.
      const r = await fetch(
        `${BASE}/annuaire?inclure_inactifs=${inclureInactifs}`,
        { credentials: "include" });
      if (!r.ok) throw new Error(`Annuaire indisponible (${r.status})`);
      const lot = (await r.json()).fiches || [];
      setFiches(lot);

      // 🔑 LES USAGES, MESURÉS AVANT LE GESTE. Sans eux, l'écran proposerait
      // « Supprimer » sur une fiche que le serveur refusera d'effacer.
      const compte = {};
      await Promise.all(lot.map(async (f) => {
        try {
          const u = await fetch(
            `${BASE}/annuaire/${encodeURIComponent(f.email)}/usages`,
            { credentials: "include" });
          if (u.ok) compte[f.email] = await u.json();
        } catch { /* le décompte manque : on n'offrira pas la suppression */ }
      }));
      setUsages(compte);
    } catch (e) {
      setErreur(e.message);
      if (onErreur) onErreur(e.message);
    } finally { setChargement(false); }
  }, [inclureInactifs, onErreur]);

  useEffect(() => { charger(); }, [charger]);

  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    const lot = q ? fiches.filter((f) => [f.organisme, f.email, f.nom,
                                          f.departement, f.fonction]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(q)))
      : fiches;
    // ⚠️ Actives d'abord, puis par organisme : une liste de gestion se lit,
    // ⛔ elle ne se fouille pas.
    return [...lot].sort((a, b) =>
      (a.actif === b.actif ? 0 : a.actif ? -1 : 1)
      || String(a.organisme || "").localeCompare(String(b.organisme || "")));
  }, [fiches, filtre]);

  const appeler = useCallback(async (libelle, url, options) => {
    setOccupe(libelle); setErreur(null); setInfo(null);
    try {
      const r = await fetch(url, { credentials: "include", ...options });
      const corps = r.status === 204 ? {} : await r.json();
      if (!r.ok) {
        // ⚠️ Le MOTIF du serveur, verbatim : il dit combien de transmissions
        // s'appuient sur l'adresse. ⛔ « Erreur » n'apprendrait rien.
        setErreur(corps.detail || `Refus (${r.status})`);
        return null;
      }
      await charger();
      return corps;
    } catch (e) { setErreur(e.message); return null; }
    finally { setOccupe(null); }
  }, [charger]);

  const enregistrer = async (fiche, adresseOrigine) => {
    const corps = { organisme: fiche.organisme, email: fiche.email,
                    type: fiche.type, nom: fiche.nom || null,
                    fonction: fiche.fonction || null,
                    departement: fiche.departement || null };
    // 🔴 L'ADRESSE A-T-ELLE CHANGÉ ? C'est la clé : la changer n'est pas une
    // mise à jour, c'est un REMPLACEMENT tracé. ⛔ Un simple ré-enregistrement
    // laisserait la fausse adresse en place, active et proposable.
    if (adresseOrigine && adresseOrigine !== fiche.email) {
      const mue = await appeler("renommer",
        `${BASE}/annuaire/${encodeURIComponent(adresseOrigine)}/adresse`,
        { method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: fiche.email }) });
      if (!mue) return;
      setInfo(`Adresse corrigée. L'ancienne (${adresseOrigine}) est désactivée `
              + "et reste liée à la nouvelle — rien n'a été détruit.");
    }
    const ok = await appeler("enregistrer", `${BASE}/annuaire`,
      { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corps) });
    if (ok) { setEdition(null); setCreation(null); }
  };

  const formulaire = (fiche, poser, adresseOrigine) => (
    <div style={{ display: "grid", gap: 8, padding: 12, borderRadius: 8,
                  background: "var(--oh-bg-elevated)",
                  border: "0.5px solid var(--oh-accent)" }}
         data-testid={adresseOrigine ? `annuaire-edition-${adresseOrigine}`
                                     : "annuaire-creation"}>
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <label style={etiquette} htmlFor="an-org">Organisme</label>
          <input id="an-org" style={champStyle} value={fiche.organisme || ""}
                 data-testid="annuaire-champ-organisme"
                 placeholder="Ex : DDT de l'Oise"
                 onChange={(e) => poser({ ...fiche, organisme: e.target.value })} />
        </div>
        <div>
          <label style={etiquette} htmlFor="an-dep">Département</label>
          <input id="an-dep" style={champStyle} value={fiche.departement || ""}
                 data-testid="annuaire-champ-departement" placeholder="Ex : 60"
                 onChange={(e) => poser({ ...fiche, departement: e.target.value })} />
        </div>
      </div>
      <div>
        <label style={etiquette} htmlFor="an-mail">Adresse</label>
        <input id="an-mail" type="email" style={champStyle} value={fiche.email || ""}
               data-testid="annuaire-champ-email"
               placeholder="adresse@exemple.gouv.fr"
               onChange={(e) => poser({ ...fiche, email: e.target.value })} />
        {adresseOrigine && adresseOrigine !== fiche.email && (
          <p style={{ ...etiquette, marginTop: 4,
                      color: "var(--oh-stat-amber-text)" }}
             data-testid="annuaire-avertissement-adresse">
            ⚠️ L&apos;adresse est la clé de la fiche. En l&apos;enregistrant,
            l&apos;ancienne sera DÉSACTIVÉE et liée à la nouvelle — ⛔ rien
            n&apos;est détruit, et la correction se défait en la réactivant.
          </p>
        )}
      </div>
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <label style={etiquette} htmlFor="an-nom">Nom du contact</label>
          <input id="an-nom" style={champStyle} value={fiche.nom || ""}
                 data-testid="annuaire-champ-nom" placeholder="Facultatif"
                 onChange={(e) => poser({ ...fiche, nom: e.target.value })} />
        </div>
        <div>
          <label style={etiquette} htmlFor="an-fct">Fonction</label>
          <input id="an-fct" style={champStyle} value={fiche.fonction || ""}
                 data-testid="annuaire-champ-fonction" placeholder="Facultatif"
                 onChange={(e) => poser({ ...fiche, fonction: e.target.value })} />
        </div>
      </div>
      {/* 🔑 Le TYPE porte une règle métier, ⛔ pas une préférence d'affichage :
          un dépôt part vers le SERVICE, une réponse à un complément vers
          l'AGENT. Chaque choix dit donc ce qu'il implique. */}
      <div style={{ display: "grid", gap: 6 }}>
        <span style={etiquette}>Type d&apos;adresse</span>
        {TYPES.map((o) => (
          <label key={o.v} style={{ display: "flex", gap: 8, alignItems: "flex-start",
                                    padding: 8, borderRadius: 8, cursor: "pointer",
                                    border: `0.5px solid ${fiche.type === o.v
                                      ? "var(--oh-accent)" : "var(--oh-border)"}`,
                                    background: fiche.type === o.v
                                      ? "var(--oh-accent-bg)" : "transparent" }}>
            <input type="radio" name="annuaire-type" value={o.v}
                   checked={fiche.type === o.v}
                   data-testid={`annuaire-type-${o.v}`}
                   onChange={() => poser({ ...fiche, type: o.v })}
                   style={{ marginTop: 3 }} />
            <span>
              <span style={{ fontSize: 13, color: "var(--oh-text)" }}>{o.t}</span>
              <span style={{ display: "block", fontSize: 12, lineHeight: 1.6,
                             color: "var(--oh-text-secondary)" }}>{o.d}</span>
            </span>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button type="button" style={btnPrimaire}
                data-testid="annuaire-enregistrer"
                disabled={occupe === "enregistrer" || !fiche.organisme || !fiche.email}
                onClick={() => enregistrer(fiche, adresseOrigine)}>
          {occupe === "enregistrer" && (
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          )}
          Enregistrer la fiche
        </button>
        <button type="button" style={btnDiscret} data-testid="annuaire-annuler"
                onClick={() => { setEdition(null); setCreation(null); }}>
          Annuler
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="annuaire-ecran">
      <p style={{ fontSize: 13, color: "var(--oh-text-secondary)",
                  lineHeight: 1.6, margin: "0 0 14px" }}>
        Les adresses vers lesquelles partent les plis d&apos;habilitation.
        Une fiche corrigée l&apos;est partout ; une fiche désactivée disparaît
        des propositions sans quitter l&apos;historique qui la nomme.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center",
                    flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 380 }}>
          <Search style={{ width: 14, height: 14, position: "absolute", left: 10,
                           top: 11, color: "var(--oh-text-muted)" }} />
          <input style={{ ...champStyle, paddingLeft: 30 }} value={filtre}
                 data-testid="annuaire-filtre"
                 placeholder="Filtrer — organisme, adresse, département…"
                 onChange={(e) => setFiltre(e.target.value)} />
        </div>
        <label style={{ ...etiquette, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={inclureInactifs}
                 data-testid="annuaire-inclure-inactifs"
                 onChange={(e) => setInclureInactifs(e.target.checked)} />
          Afficher les fiches désactivées
        </label>
        <button type="button" style={btnPrimaire} data-testid="annuaire-nouvelle"
                onClick={() => { setCreation({ ...FICHE_VIDE }); setEdition(null); }}>
          <UserPlus style={{ width: 14, height: 14 }} /> Nouvelle fiche
        </button>
      </div>

      {erreur && (
        <div style={{ padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12,
                      background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", lineHeight: 1.6 }}
             data-testid="annuaire-erreur">{erreur}</div>
      )}
      {info && (
        <div style={{ padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12,
                      background: "var(--oh-info-bg)", color: "var(--oh-info-text)",
                      lineHeight: 1.6 }} data-testid="annuaire-info">{info}</div>
      )}

      {creation && (
        <div style={{ marginBottom: 14 }}>{formulaire(creation, setCreation, null)}</div>
      )}

      {chargement && (
        <p style={{ ...etiquette, display: "flex", gap: 6, alignItems: "center" }}>
          <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          Lecture de l&apos;annuaire…
        </p>
      )}

      {!chargement && visibles.length === 0 && (
        <div style={{ padding: 16, borderRadius: 12, fontSize: 13, lineHeight: 1.6,
                      background: "var(--oh-bg-elevated)",
                      border: "0.5px solid var(--oh-border)",
                      color: "var(--oh-text-secondary)" }}
             data-testid="annuaire-vide">
          {/* ⚠️ On dit ce que ça SIGNIFIE, ⛔ pas « aucun résultat » : un annuaire
              vide n'est pas une panne, et la proposition le dira aussi. */}
          {filtre
            ? "Aucune fiche ne correspond à ce filtre."
            : "L'annuaire est vide. Tant qu'il l'est, le parcours d'envoi ne "
              + "proposera aucun destinataire et demandera de saisir l'adresse "
              + "à la main — ⛔ OmniHub n'invente aucune adresse."}
        </div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {visibles.map((f) => {
          const u = usages[f.email];
          const enEdition = edition && edition._origine === f.email;
          if (enEdition) {
            return (
              <div key={f.email}>{formulaire(edition, setEdition, f.email)}</div>
            );
          }
          const ton = TON_TYPE[f.type] || TON_TYPE.generique;
          return (
            <div key={f.email} data-testid={`annuaire-ligne-${f.email}`}
                 style={{ padding: 12, borderRadius: 12,
                          background: "var(--oh-bg-elevated)",
                          border: "0.5px solid var(--oh-border)",
                          opacity: f.actif ? 1 : 0.62 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8,
                            alignItems: "center" }}>
                <span style={{ padding: "2px 7px", borderRadius: 6, fontSize: 11,
                               background: ton.bg, color: ton.text }}>
                  {f.type === "nominatif" ? "agent" : "service"}
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--oh-text)" }}>
                  {f.organisme}
                </span>
                {f.departement && (
                  <span style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
                    · dép. {f.departement}
                  </span>
                )}
                {!f.actif && (
                  <span style={{ padding: "2px 7px", borderRadius: 6, fontSize: 11,
                                 background: "var(--oh-stat-amber-bg)",
                                 color: "var(--oh-stat-amber-text)" }}
                        data-testid={`annuaire-inactive-${f.email}`}>
                    désactivée — hors des propositions
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--oh-text-secondary)",
                            marginTop: 4, lineHeight: 1.6 }}>
                {f.email}
                {f.nom ? ` · ${f.nom}` : ""}{f.fonction ? ` (${f.fonction})` : ""}
              </div>
              {/* 🔑 LE LIEN DE REMPLACEMENT, LISIBLE DANS LES DEUX SENS. Sans
                  lui, une fiche désactivée après correction serait un mystère. */}
              {(f.remplacee_par || f.remplace) && (
                <div style={{ fontSize: 12, color: "var(--oh-text-muted)", marginTop: 3 }}
                     data-testid={`annuaire-lien-${f.email}`}>
                  {f.remplacee_par ? `remplacée par ${f.remplacee_par}` : ""}
                  {f.remplace ? `remplace ${f.remplace}` : ""}
                </div>
              )}
              {u && (
                <div style={{ fontSize: 12, marginTop: 4, display: "flex",
                              alignItems: "center", gap: 5,
                              color: "var(--oh-text-muted)" }}
                     data-testid={`annuaire-usages-${f.email}`}>
                  <Info style={{ width: 12, height: 12, flexShrink: 0 }} />
                  {/* 🔑 LE MOTIF VIENT DU SERVEUR, verbatim — et c'est la MÊME
                      fonction qui refuse la suppression. ⛔ Recopier la règle
                      ici la ferait diverger : l'écran dirait « effaçable » là
                      où le serveur refuse. */}
                  {u.effacable ? "jamais utilisée — effaçable" : u.motif}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <button type="button" style={btnDiscret}
                        data-testid={`annuaire-editer-${f.email}`}
                        onClick={() => { setEdition({ ...f, _origine: f.email });
                                         setCreation(null); }}>
                  <Pencil style={{ width: 13, height: 13 }} /> Modifier
                </button>
                <button type="button" style={btnDiscret}
                        disabled={occupe === "bascule"}
                        data-testid={`annuaire-bascule-${f.email}`}
                        onClick={() => appeler("bascule",
                          `${BASE}/annuaire/${encodeURIComponent(f.email)}`,
                          { method: "PATCH",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ actif: !f.actif }) })}>
                  {f.actif
                    ? <><EyeOff style={{ width: 13, height: 13 }} /> Désactiver</>
                    : <><Eye style={{ width: 13, height: 13 }} /> Réactiver</>}
                </button>
                {/* 🔴 « Supprimer » n'apparaît QUE si le serveur a dit que la
                    fiche n'a jamais servi. ⛔ Offrir un bouton qui sera refusé
                    ferait passer une garde pour une panne. */}
                {u && u.effacable && (
                  <button type="button"
                          style={{ ...btnDiscret, color: "var(--oh-stat-red-text)",
                                   borderColor: "var(--oh-stat-red-border)" }}
                          disabled={occupe === "supprimer"}
                          data-testid={`annuaire-supprimer-${f.email}`}
                          onClick={() => {
                            if (!window.confirm(
                              `Supprimer définitivement ${f.email} ?\n\n`
                              + "Aucune transmission ne la nomme : rien ne sera "
                              + "rendu illisible. ⛔ Ce geste ne s'annule pas.")) return;
                            appeler("supprimer",
                              `${BASE}/annuaire/${encodeURIComponent(f.email)}`,
                              { method: "DELETE" });
                          }}>
                    <Trash2 style={{ width: 13, height: 13 }} /> Supprimer
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
